import React from 'react';
import { Link } from 'react-router-dom';
import { FaGhost, FaRocket, FaTrophy, FaChartLine, FaUsers } from 'react-icons/fa';
import SEOHead from '../components/SEOHead';
import { contentTypes } from '../utils/designTokens';

const Home = () => {
  return (
    <div className="min-h-screen">
      <SEOHead
        title="Boop - AI Prompts, Agents & MCP Servers"
        description="Discover, share, and rate AI prompts, intelligent agents, and MCP servers. Join our community of AI enthusiasts and unlock achievements."
        url={window.location.origin}
        type="website"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-boop-primary via-boop-secondary to-boop-accent text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FaGhost className="text-6xl mx-auto mb-6 animate-float" />
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Welcome to Boop
          </h1>
          <p className="text-xl md:text-2xl mb-4 text-gray-100">
            Your AI Content Hub - Prompts, Agents & MCP Servers
          </p>
          <p className="text-lg mb-8 text-gray-200 max-w-3xl mx-auto">
            Discover, share, and rate AI prompts, intelligent agents, and MCP servers.
            Join our community and level up your AI toolkit.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/signup"
              className="bg-white text-boop-primary px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition transform hover:scale-105"
            >
              Get Started Free
            </Link>
            <Link
              to="/dashboard"
              className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-boop-primary transition"
            >
              Explore Content
            </Link>
          </div>
        </div>
      </section>

      {/* Content Types Section */}
      <section className="py-20 bg-white dark:bg-spooky-bg-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-4 text-gray-900 dark:text-spooky-ghost-white">
            Everything You Need for AI
          </h2>
          <p className="text-center text-gray-600 dark:text-gray-300 mb-12 max-w-2xl mx-auto">
            Boop brings together three essential AI content types in one platform
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Prompts Card */}
            <Link
              to="/dashboard?type=prompt"
              className="group bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-900/20 dark:to-violet-900/20 rounded-xl p-8 border-2 border-indigo-100 dark:border-indigo-800 hover:border-indigo-300 dark:hover:border-indigo-600 transition-all duration-300 hover:shadow-xl"
            >
              <div className="text-5xl mb-4">{contentTypes.prompt.icon}</div>
              <h3 className="text-2xl font-bold mb-3 text-indigo-900 dark:text-indigo-300">
                {contentTypes.prompt.namePlural}
              </h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Discover and share carefully crafted AI prompts for ChatGPT, Claude, and other LLMs.
                Perfect for writers, developers, and creators.
              </p>
              <div className="flex items-center text-indigo-600 dark:text-indigo-400 font-semibold group-hover:translate-x-1 transition-transform">
                Browse Prompts →
              </div>
            </Link>

            {/* AI Agents Card */}
            <Link
              to="/dashboard?type=agent"
              className="group bg-gradient-to-br from-pink-50 to-rose-50 dark:from-pink-900/20 dark:to-rose-900/20 rounded-xl p-8 border-2 border-pink-100 dark:border-pink-800 hover:border-pink-300 dark:hover:border-pink-600 transition-all duration-300 hover:shadow-xl"
            >
              <div className="text-5xl mb-4">{contentTypes.agent.icon}</div>
              <h3 className="text-2xl font-bold mb-3 text-pink-900 dark:text-pink-300">
                {contentTypes.agent.namePlural}
              </h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Explore intelligent AI agents with custom tools, memory, and capabilities.
                Build autonomous assistants for any task.
              </p>
              <div className="flex items-center text-pink-600 dark:text-pink-400 font-semibold group-hover:translate-x-1 transition-transform">
                Browse Agents →
              </div>
            </Link>

            {/* MCP Servers Card */}
            <Link
              to="/dashboard?type=mcp_server"
              className="group bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 rounded-xl p-8 border-2 border-cyan-100 dark:border-cyan-800 hover:border-cyan-300 dark:hover:border-cyan-600 transition-all duration-300 hover:shadow-xl"
            >
              <div className="text-5xl mb-4">{contentTypes.mcp_server.icon}</div>
              <h3 className="text-2xl font-bold mb-3 text-cyan-900 dark:text-cyan-300">
                {contentTypes.mcp_server.namePlural}
              </h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Connect AI to external tools and services with Model Context Protocol servers.
                Extend AI capabilities infinitely.
              </p>
              <div className="flex items-center text-cyan-600 dark:text-cyan-400 font-semibold group-hover:translate-x-1 transition-transform">
                Browse Servers →
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50 dark:bg-spooky-bg-darker">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900 dark:text-spooky-ghost-white">
            Why Boop?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-boop-light dark:bg-spooky-bg-medium rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <FaRocket className="text-3xl text-boop-primary dark:text-spooky-ghost-glow" />
              </div>
              <h3 className="text-xl font-semibold mb-2 dark:text-spooky-ghost-white">Share AI Content</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Post prompts, agents, and MCP servers. Get feedback from experts.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-boop-light dark:bg-spooky-bg-medium rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <FaTrophy className="text-3xl text-boop-secondary dark:text-spooky-ghost-glow" />
              </div>
              <h3 className="text-xl font-semibold mb-2 dark:text-spooky-ghost-white">Earn Badges</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Unlock achievements and climb from Beginner to Professional
              </p>
            </div>

            <div className="text-center">
              <div className="bg-boop-light dark:bg-spooky-bg-medium rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <FaChartLine className="text-3xl text-boop-accent dark:text-spooky-ghost-glow" />
              </div>
              <h3 className="text-xl font-semibold mb-2 dark:text-spooky-ghost-white">Optimize & Analyze</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Free token analyzer and tools to improve your AI content
              </p>
            </div>

            <div className="text-center">
              <div className="bg-boop-light dark:bg-spooky-bg-medium rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <FaUsers className="text-3xl text-boop-ghost dark:text-spooky-ghost-glow" />
              </div>
              <h3 className="text-xl font-semibold mb-2 dark:text-spooky-ghost-white">Join Community</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Connect with AI enthusiasts, developers, and creators
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white dark:bg-spooky-bg-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900 dark:text-spooky-ghost-white">
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white dark:bg-spooky-bg-medium p-6 rounded-lg shadow-md dark:shadow-spooky-purple-mist/20">
              <div className="text-4xl font-bold text-boop-primary dark:text-spooky-ghost-glow mb-4">1</div>
              <h3 className="text-xl font-semibold mb-2 dark:text-spooky-ghost-white">Create & Share</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Sign up for free and share your AI prompts, intelligent agents, or MCP servers with the community
              </p>
            </div>

            <div className="bg-white dark:bg-spooky-bg-medium p-6 rounded-lg shadow-md dark:shadow-spooky-purple-mist/20">
              <div className="text-4xl font-bold text-boop-secondary dark:text-spooky-ghost-glow mb-4">2</div>
              <h3 className="text-xl font-semibold mb-2 dark:text-spooky-ghost-white">Rate & Review</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Give 1-10 "boop" ratings on quality dimensions. Help others discover the best content.
              </p>
            </div>

            <div className="bg-white dark:bg-spooky-bg-medium p-6 rounded-lg shadow-md dark:shadow-spooky-purple-mist/20">
              <div className="text-4xl font-bold text-boop-accent dark:text-spooky-ghost-glow mb-4">3</div>
              <h3 className="text-xl font-semibold mb-2 dark:text-spooky-ghost-white">Level Up</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Earn badges, climb from Beginner to Professional, and compete for weekly awards
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-boop-primary to-boop-secondary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Level Up Your AI Toolkit?
          </h2>
          <p className="text-xl mb-8">
            Join our growing community of AI enthusiasts, developers, and creators today!
          </p>
          <Link
            to="/signup"
            className="bg-white text-boop-primary px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition transform hover:scale-105 inline-block"
          >
            Sign Up Now - It's Free!
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
